import { useState, useCallback } from 'react';
import { Upload, FileText, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUpload = ({ location, token, onUploadSuccess }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragging(true);
    } else if (e.type === 'dragleave') {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'text/csv') {
      processFile(droppedFile);
    } else {
      setError('Nahrajte prosím platný CSV soubor');
    }
  }, []);

  const handleFileInput = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      processFile(selectedFile);
    } else {
      setError('Nahrajte prosím platný CSV soubor');
    }
  };

  const processFile = (csvFile) => {
    setFile(csvFile);
    setError(null);
    setSuccess(null);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n');
      const headers = lines[0].split(',');
      const previewData = lines.slice(1, 6).filter(line => line.trim()).map(line => {
        const values = line.split(',');
        const obj = {};
        headers.forEach((header, i) => {
          obj[header.trim()] = values[i]?.trim() || '';
        });
        return obj;
      });
      setPreview(previewData);
    };
    reader.readAsText(csvFile);
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/inventory/upload?location=${location}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setSuccess(`Úspěšně nahráno ${response.data.items_processed} položek!`);
      setFile(null);
      setPreview([]);
      
      if (onUploadSuccess) {
        setTimeout(() => onUploadSuccess(), 1000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Nahrávání selhalo. Zkuste to prosím znovu.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-upload">
      {/* Upload Zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer bg-white/5 ${
          isDragging ? 'border-primary bg-primary/10' : 'border-white/20 hover:border-primary/50'
        }`}
        data-testid="upload-dropzone"
      >
        <input
          type="file"
          accept=".csv"
          onChange={handleFileInput}
          className="hidden"
          id="file-upload"
          data-testid="file-input"
        />
        <label htmlFor="file-upload" className="cursor-pointer">
          <Upload className="w-16 h-16 mx-auto mb-4 text-primary" />
          <h3 className="text-xl font-heading font-semibold mb-2 text-white">
            {file ? file.name : 'Přetáhněte CSV soubor sem'}
          </h3>
          <p className="text-muted-foreground">nebo klikněte pro výběr souboru</p>
        </label>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-center gap-3" data-testid="upload-error">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-4 flex items-center gap-3" data-testid="upload-success">
          <CheckCircle2 className="w-5 h-5 text-green-500" />
          <p className="text-green-400">{success}</p>
        </div>
      )}

      {/* Preview */}
      {preview.length > 0 && (
        <div className="space-y-4" data-testid="upload-preview">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-heading font-semibold flex items-center gap-2 text-white">
              <FileText className="w-5 h-5 text-primary" />
              Náhled dat (prvních 5 řádků)
            </h3>
            <Button
              onClick={handleUpload}
              disabled={uploading}
              className="bg-primary hover:bg-primary/90 text-white font-semibold rounded-full px-6"
              data-testid="upload-confirm-button"
            >
              {uploading ? 'Nahrávání...' : 'Potvrdit nahrání'}
            </Button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50">
                <tr>
                  <th className="px-4 py-3 text-left text-accent font-semibold">Slot ID</th>
                  <th className="px-4 py-3 text-left text-accent font-semibold">Název</th>
                  <th className="px-4 py-3 text-left text-accent font-semibold">Množství</th>
                  <th className="px-4 py-3 text-left text-accent font-semibold">Cena</th>
                  <th className="px-4 py-3 text-left text-accent font-semibold">URL obrázku</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((row, i) => (
                  <tr key={i} className="border-t border-white/10 hover:bg-white/5">
                    <td className="px-4 py-3 text-white font-mono">{row.slot_id}</td>
                    <td className="px-4 py-3 text-white">{row.item_name}</td>
                    <td className="px-4 py-3 text-white">{row.quantity}</td>
                    <td className="px-4 py-3 text-accent font-semibold">{row.price} Kč</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs truncate max-w-[200px]">
                      {row.custom_image_url || 'API náhled'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4">
            <p className="text-yellow-400 text-sm">
              ⚠️ <strong>Upozornění:</strong> Potvrzením nahrajete data a <strong>SMAŽETE všechny</strong> existující položky pro lokaci "{location}". Tato akce je nevratná.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUpload;